/**
 * Pure mapping between the VisionProjects proto messages (enum-typed role, Timestamp
 * instants) and the legacy dashboard project shape the UI reads (ProjectCard /
 * ProjectListRow: `id`, `name`, `description`, `user_role`, `owner_name`,
 * `category_count`, `created_at`, `updated_at`). Kept pure + isolated so it is
 * independently testable and the dashboard stays unaware of the transport's typing.
 *
 * The role enum flows inbound only. OWNER and FULL_ACCESS both map to the legacy
 * 'full_access' (the legacy backend collapses the owner into 'full_access', and the card's
 * canManage check is `user_role === 'full_access'`). UNKNOWN is rejected — the backend must
 * never return it.
 */

import { timestampDate } from '@bufbuild/protobuf/wkt';
import { VisionProjectRole } from '../gen/vision/projects/v1/vision_project_pb';

const USER_ROLE_FROM_PROTO = {
  [VisionProjectRole.OWNER]: 'full_access',
  [VisionProjectRole.FULL_ACCESS]: 'full_access',
  [VisionProjectRole.EDIT]: 'edit',
  [VisionProjectRole.VIEW_ONLY]: 'view_only',
};

function lookup(map, key, label) {
  if (!Object.prototype.hasOwnProperty.call(map, key)) {
    throw new Error(`Unmappable ${label}: ${key}`);
  }
  return map[key];
}

function isoOrNull(timestamp) {
  return timestamp ? timestampDate(timestamp).toISOString() : null;
}

export function listItemToProject(item) {
  return {
    id: item.projectId,
    name: item.name,
    description: item.description,
    user_role: lookup(USER_ROLE_FROM_PROTO, item.myRole, 'my_role'),
    owner_name: item.ownerName,
    category_count: item.locationCount,
    categories: (item.locationSummaries || []).map((summary) => ({
      id: summary.locationId,
      name: summary.name,
      image_count: summary.imageCount,
    })),
    cover_thumbnail: item.coverThumbnailUrl || null,
    image_count: item.totalImageCount,
    generation_count: item.generationCount,
    created_at: isoOrNull(item.createdAt),
    updated_at: isoOrNull(item.updatedAt),
  };
}

export function listResponseToProjects(response) {
  const nextPageToken = response.pagination?.nextPageToken || '';
  return {
    projects: (response.result?.items || []).map(listItemToProject),
    nextPageToken,
    hasMore: nextPageToken !== '',
  };
}

export function createdProjectToProject(project) {
  return {
    id: project.projectId,
    name: project.name,
    description: project.description,
    user_role: 'full_access',
    category_count: 0,
    created_at: isoOrNull(project.createdAt),
    updated_at: isoOrNull(project.updatedAt),
  };
}

function locationToCategory(location, projectId) {
  return {
    id: location.locationId,
    name: location.name,
    address: location.address || null,
    sort_order: location.sortOrder,
    project_id: projectId,
    image_count: 0,
    created_at: isoOrNull(location.createdAt),
    updated_at: isoOrNull(location.updatedAt),
  };
}

export function detailResponseToLoadProjectShape(response) {
  const result = response.result;
  if (!result || !result.project) {
    throw new Error('GetVisionProject returned no project');
  }
  const detail = result.project;
  const user_role = lookup(USER_ROLE_FROM_PROTO, result.myRole, 'my_role');

  const project = {
    id: detail.projectId,
    name: detail.name,
    description: detail.description,
    owner_account_id: detail.ownerAccountId,
    created_by: detail.ownerAccountId,
    project_credits: Number(detail.projectCredits),
    low_credits_notified: detail.lowCreditsNotified,
    visionboard_rules: detail.visionboardRules,
    created_at: isoOrNull(detail.createdAt),
    updated_at: isoOrNull(detail.updatedAt),
  };

  const categories = (detail.locations || []).map((location) =>
    locationToCategory(location, detail.projectId),
  );

  return {
    project,
    categories,
    images: [],
    user_role,
    project_credits: project.project_credits,
    cast: detail.cast || [],
    props: detail.props || [],
    shotlist_config: detail.shotlistConfig || null,
    shotlist_custom_presets: detail.shotlistCustomPresets || [],
  };
}
